import React, { useState, useEffect, useRef } from 'react';
import {
  Editor,
  EditorState,
  ContentState,
  RichUtils,
  DraftHandleValue,
  convertToRaw
} from 'draft-js';
import htmlToDraft from 'html-to-draftjs';
import draftToHtml from 'draftjs-to-html'
import 'draft-js/dist/Draft.css';

interface DraftEditorProps {
  initialHtml: string;
  onHtmlChange: (html: string) => void;
}

export const DraftEditor: React.FC<DraftEditorProps> = ({
  initialHtml,
  onHtmlChange,
}) => {
  // 1) create EditorState from initialHtml
  const createStateFromHtml = (html: string) => {
    if (!html) {
      return EditorState.createEmpty();
    } 
    const blocksFromHtml = htmlToDraft(html);
    const { contentBlocks, entityMap } = blocksFromHtml;
    const contentState = ContentState.createFromBlockArray(
      contentBlocks,
      entityMap
    );
    return EditorState.createWithContent(contentState);
  };

  const [editorState, setEditorState] = useState<EditorState>(() => {
    return createStateFromHtml(initialHtml)
  }
  );

  // 2) whenever editorState changes, emit HTML
  // Listen for changes in initialHtml prop
  useEffect(() => {
    // Only update if initialHtml has changed and is different from current content
    const currentHtml = draftToHtml(convertToRaw(editorState.getCurrentContent()));
    
    if (initialHtml !== currentHtml) {
      console.log('Updating editor content from prop change:', initialHtml);
      setEditorState(createStateFromHtml(initialHtml || ''));
    }
  }, [initialHtml]);

  const onEditorStateChange = (newEditorState: EditorState) => {
    setEditorState(newEditorState);
    
    // Convert content to HTML and call the callback
    const html = draftToHtml(convertToRaw(newEditorState.getCurrentContent()));
    onHtmlChange(html);
  };

  // 3) handlers for toolbar buttons
  const toggleInline = (style: string) => {
    setEditorState(RichUtils.toggleInlineStyle(editorState, style));
  };
  const toggleBlock = (block: string) => {
    setEditorState(RichUtils.toggleBlockType(editorState, block));
  };

  // 4) allow tab for list indenting etc.
  const handleKeyCommand = (
    command: string,
    state: EditorState
  ): DraftHandleValue => {
    const newState = RichUtils.handleKeyCommand(state, command);
    if (newState) {
      setEditorState(newState);
      return 'handled';
    }
    return 'not-handled';
  };

  const activeStyles = editorState.getCurrentInlineStyle();
  const blockType = editorState
    .getCurrentContent()
    .getBlockForKey(editorState.getSelection().getStartKey())
    .getType();

  return (
    <div style={{ border: '1px solid #ccc', padding: 8, width: '100%', backgroundColor: '#fff' }}>
      <div style={{ marginBottom: 8 }}>
        {/* Inline style buttons */}
        <button onMouseDown={e => { e.preventDefault(); toggleInline('BOLD'); }}
          style={{
            fontWeight: 'bold',
            border: 'none',
            fontSize: 16,
            cursor: 'pointer',
            marginRight: 16,
            padding: '2px 0',
            display: 'inline-block', color: activeStyles.has('BOLD') ? '#5890ff' : '#999',
          }}
        >
          Bold
        </button>
        <button onMouseDown={e => { e.preventDefault(); toggleInline('ITALIC'); }}
          style={{
            fontStyle: 'italic',
            border: 'none',
            fontSize: 16,
            cursor: 'pointer',
            marginRight: 16,
            padding: '2px 0',
            display: 'inline-block', color: activeStyles.has('ITALIC') ? '#5890ff' : '#999',
          }}
        >
          Italic
        </button>
        <button onMouseDown={e => { e.preventDefault(); toggleInline('UNDERLINE'); }}
          style={{
            textDecoration: 'underline',
            border: 'none',
            fontSize: 16,
            cursor: 'pointer',
            marginRight: 16,
            padding: '2px 0',
            display: 'inline-block', color: activeStyles.has('UNDERLINE') ? '#5890ff' : '#999',
          }}
        >
          Underline
        </button>
        {/* Block type buttons */}
        <button onMouseDown={e => { e.preventDefault(); toggleBlock('unordered-list-item'); }}
          style={{
            border: 'none',
            fontSize: 16,
            cursor: 'pointer',
            marginRight: 16,
            padding: '2px 0',
            display: 'inline-block',
            color: blockType === 'unordered-list-item'
              ? '#5890ff' : '#999',
          }}
        >
          • List
        </button>
        <button onMouseDown={e => { e.preventDefault(); toggleBlock('ordered-list-item'); }}
          style={{
            border: 'none',
            fontSize: 16,
            cursor: 'pointer',
            marginRight: 16,
            padding: '2px 0',
            display: 'inline-block',
            color: blockType === 'ordered-list-item'
              ? '#5890ff' : '#999',
          }}
        >
          1. List
        </button>
      </div>
      {/* 5) line between */}
      <div style={{ borderBottom: '1px solid #ccc', marginBottom: 8 }}></div>
      {/* 6) editor */}
      <div style={{ minHeight: 100, cursor: 'text' }} onClick={() => {/* focus editor if needed */ }}>
        <Editor
          editorState={editorState}
          onChange={onEditorStateChange}
          handleKeyCommand={handleKeyCommand}
          placeholder="לחץ כאן לתחילת הקלדה..."
          stripPastedStyles={true}
        />
      </div>
    </div>
  );
};

